import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { quotationsAPI, customersAPI, costingsAPI, partsAPI, inquiriesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Eye, Check, Send, Download, Loader2,
  Search, Globe, IndianRupee, Calendar,
  CheckCircle, Copy, Mail, FileText, FileOutput
} from 'lucide-react';
import { PaginationControls } from '../components/PaginationControls';
import { Checkbox } from '../components/ui/checkbox';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/bulk/BulkActionBar';
import QuotationHeaderForm from '../components/quotations/QuotationHeaderForm';
import QuotationItemsBuilder from '../components/quotations/QuotationItemsBuilder';
import { InquiryQuoteLinesPreview } from '../components/quotations/InquiryQuoteLinesPreview';
import { STATUS_COLORS, CURRENCIES } from '../components/quotations/constants';

const QUOTATION_CSV_COLUMNS = [
  { header: 'Quote No', value: (row) => row.quotation_number },
  { header: 'Revision', value: (row) => row.revision_number || 0 },
  { header: 'Customer', value: (row) => row.customer_name },
  { header: 'Customer Code', value: (row) => row.customer_code },
  { header: 'Items', value: (row) => row.items?.length || 0 },
  { header: 'Currency', value: (row) => row.currency || 'INR' },
  { header: 'Total', value: (row) => row.total_amount },
  { header: 'Status', value: (row) => row.status },
  { header: 'Created', value: (row) => row.created_at },
];


export default function QuotationsPage() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(listParams.get('status') || '');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [limit, setLimit] = useState(50);
  const selection = useBulkSelection(quotations);

  const fetchQuotations = useCallback(async () => {
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const response = await quotationsAPI.list(params);
      const items = response.data.items || response.data.data || [];
      setQuotations(items);
      selection.remember(items);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, page, limit]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, limit]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  const formatCurrency = (amount, currency = 'INR') => {
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    return `${symbols[currency] || currency} ${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">Quotations</h1>
          <p className="text-muted-foreground">Create and manage customer quotations</p>
        </div>
        <Button className="btn-primary" onClick={() => navigate('/quotations/new')} data-testid="new-quotation-btn">
          <Plus className="mr-2 h-4 w-4" />New Quotation
        </Button>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by quote no, customer, part..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="quotation-search-input"
              />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="released">Released</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="outline">{total} quotations</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <BulkActionBar
            entity="quotations"
            selectedIds={selection.selectedIds}
            selectedRows={selection.selectedRows}
            onClear={selection.clear}
            onDone={fetchQuotations}
            csvColumns={QUOTATION_CSV_COLUMNS}
            csvPrefix="quotations"
          />
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : quotations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileOutput className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No quotations found</p>
            </div>
          ) : (
            <div className="table-wrapper overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selection.allOnPageSelected}
                        onCheckedChange={selection.togglePage}
                        aria-label="Select all quotations on this page"
                        data-testid="quotations-select-all-page"
                      />
                    </TableHead>
                    <TableHead>Quote No</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotations.map((q) => (
                    <TableRow key={q.id} className="table-row" data-state={selection.isSelected(q.id) ? 'selected' : undefined}>
                      <TableCell>
                        <Checkbox
                          checked={selection.isSelected(q.id)}
                          onCheckedChange={() => selection.toggleOne(q)}
                          aria-label={`Select quotation ${q.quotation_number}`}
                          data-testid={`quotations-row-checkbox-${q.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-medium">
                        <div className="flex items-center gap-2">
                          {q.quotation_number}
                          {q.revision_number > 0 && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                              R{q.revision_number}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{q.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{q.customer_code}</p>
                        </div>
                      </TableCell>
                      <TableCell>{q.items?.length || 0} items</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {q.currency || 'INR'}
                          {q.exchange_rate && q.exchange_rate !== 1 && (
                            <span className="ml-1 text-xs">@{q.exchange_rate}</span>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono font-medium">
                        {formatCurrency(q.total_amount, q.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[q.status] || 'bg-gray-100'}>{q.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(q.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/quotations/${q.id}`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          <PaginationControls
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onLimitChange={setLimit}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// New Quotation Page (Shopping Cart Builder)

export function NewQuotationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inquiryId = searchParams.get('inquiry');
  const inquiryItemIds = (searchParams.get('items') || '').split(',').filter(Boolean);
  const [step, setStep] = useState(1); // 1: Header, 2: Items
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [quotationId, setQuotationId] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inquiry, setInquiry] = useState(null);
  const [loadingInquiry, setLoadingInquiry] = useState(Boolean(inquiryId));

  // Header form
  const [headerData, setHeaderData] = useState({
    currency: 'INR',
    exchange_rate: 1,
    handling_charge: 0,
    validity_days: 30,
    delivery_terms: '',
    payment_terms: '',
    incoterms: '',
    tax_percentage: 0,
    discount_percentage: 0,
    notes: '',
    terms_conditions: '',
    inquiry_number: '',
    inquiry_date: '',
    contact_person: '', // Editable contact name per-quotation
  });

  const searchCustomers = useCallback(async () => {
    try {
      const res = await customersAPI.list({ search: customerSearch, is_active: true });
      setCustomers(res.data || []);
    } catch (e) { }
  }, [customerSearch]);

  useEffect(() => {
    if (customerSearch.length >= 2) {
      searchCustomers();
    }
  }, [customerSearch, searchCustomers]);

  useEffect(() => {
    if (selectedCustomer) {
      setHeaderData(prev => ({
        ...prev,
        currency: selectedCustomer.currency || 'INR',
        payment_terms: selectedCustomer.payment_terms || '',
        terms_conditions: selectedCustomer.default_terms || '',
        contact_person: prev.contact_person || selectedCustomer.contact_name || selectedCustomer.contact_person || '',
      }));
    }
  }, [selectedCustomer]);

  // Inquiry-driven review step: load the inquiry, lock its customer and pre-fill the terms
  useEffect(() => {
    if (!inquiryId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await inquiriesAPI.get(inquiryId);
        if (cancelled) return;
        const detail = res.data;
        setInquiry(detail);
        if (detail.customer_id) {
          const customerRes = await customersAPI.get(detail.customer_id);
          if (!cancelled) setSelectedCustomer(customerRes.data);
        }
        setHeaderData(prev => ({
          ...prev,
          inquiry_number: detail.customer_inquiry_number || detail.inquiry_number || '',
          inquiry_date: (detail.customer_inquiry_date || '').slice(0, 10),
          contact_person: detail.customer_contact_person || prev.contact_person,
        }));
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Could not load the inquiry');
      } finally {
        if (!cancelled) setLoadingInquiry(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inquiryId]);

  const inquiryItems = (inquiry?.products || []).filter(
    (product) => !inquiryItemIds.length || inquiryItemIds.includes(product.id)
  );

  const createQuotationFromInquiry = async () => {
    setSaving(true);
    try {
      const res = await inquiriesAPI.createQuotation(inquiryId, {
        ...headerData,
        inquiry_product_ids: inquiryItems.map((product) => product.id),
      });
      setQuotationId(res.data.id);
      toast.success(`Draft quotation ${res.data.quotation_number} created`);
      const qRes = await quotationsAPI.get(res.data.id);
      setQuotation(qRes.data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Quotation creation failed');
    } finally {
      setSaving(false);
    }
  };

  const createQuotationHeader = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    setSaving(true);
    try {
      const data = {
        customer_id: selectedCustomer.id,
        ...headerData,
      };
      const res = await quotationsAPI.create(data);
      setQuotationId(res.data.id);
      toast.success(`Quotation ${res.data.quotation_number} created`);
      setStep(2);
      // Fetch the created quotation
      const qRes = await quotationsAPI.get(res.data.id);
      setQuotation(qRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
            {inquiryId ? 'Review & Create Quotation' : 'New Quotation'}
          </h1>
          <p className="text-muted-foreground">
            {step === 1
              ? (inquiryId
                ? `Step 1: Confirm currency and terms for ${inquiry?.inquiry_number || 'this inquiry'}`
                : 'Step 1: Select customer and set terms')
              : 'Step 2: Add line items'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-muted'}`}>1</div>
          <div className={`w-12 h-1 ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-muted'}`}>2</div>
        </div>
      </div>

      {step === 1 && loadingInquiry && (
        <div className="flex items-center gap-2 py-12 text-muted-foreground" data-testid="inquiry-quote-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading the inquiry…
        </div>
      )}

      {step === 1 && !loadingInquiry && (
        <>
          <QuotationHeaderForm
            customers={customers}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            headerData={headerData}
            setHeaderData={setHeaderData}
            onSubmit={inquiryId ? createQuotationFromInquiry : createQuotationHeader}
            saving={saving}
            onCancel={() => navigate(inquiryId ? `/inquiries/${inquiryId}` : '/quotations')}
            customerLocked={Boolean(inquiryId)}
            submitLabel={inquiryId ? 'Create draft & review items' : 'Continue to Add Items'}
          />
          {inquiryId && inquiryItems.length > 0 && (
            <InquiryQuoteLinesPreview items={inquiryItems} />
          )}
        </>
      )}

      {step === 2 && quotationId && (
        <QuotationItemsBuilder
          quotationId={quotationId}
          quotation={quotation}
          setQuotation={setQuotation}
          onComplete={() => navigate(`/quotations/${quotationId}`)}
        />
      )}
    </div>
  );
}
